<template>
  <div>
    <div class="headline5 mb-4">
      {{ $t(`modals.addGlobalMspPolicy.policies.devicesSettings.title`) }}
    </div>
    <devices-settings-tab-content
      :action-not-allowed="disabled"
      :loading="false"
      :labels="availableLabels"
      v-model="localValue"
      @on-user-based-auth-clicked="onUserBasedAuthClicked()"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, watch, type PropType, onMounted } from "vue";
import { defaultEmptyPolicies, GlobalPolicySection } from "@/constants/msp.ts";
import cloneDeep from "lodash/cloneDeep";
import { type GlobalPolicyConfig } from "@/_store/msp/global-policies/msp-global-policies.module.ts";
import DevicesSettingsTabContent from "@/components/settings/DevicesSettingsTabContent.vue";
import { storeToRefs } from "pinia";
import { useMspDevicesStore } from "@/_store/msp/devices/msp-devices.module.ts";
import { DeviceSettingsActions } from "@/constants/devices.ts";
import { useDialogsStore } from "@/_store/dialogs.module.ts";
import { confirmationDialogsConfigConstructor } from "@/_helpers/dialog-helpers.ts";

export default defineComponent({
  components: { DevicesSettingsTabContent },
  props: {
    data: {
      type: Object as PropType<GlobalPolicyConfig>,
      required: true,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ["update:data"],
  setup(props, { emit }) {
    const mspDevicesStore = useMspDevicesStore();
    const dialogsStore = useDialogsStore();
    const { getLabels } = mspDevicesStore;
    const { availableLabels } = storeToRefs(mspDevicesStore);

    const localValue = ref(
      cloneDeep(
        props.data[GlobalPolicySection.DEVICE_SETTINGS] ??
          defaultEmptyPolicies[GlobalPolicySection.DEVICE_SETTINGS]
      )
    );

    onMounted(async () => {
      await getLabels();
    });

    const onUserBasedAuthClicked = () => {
      if (localValue.value.enableUserBasedAuthentication) {
        localValue.value.enableUserBasedAuthentication =
          !localValue.value.enableUserBasedAuthentication;
      } else {
        const dialogConfig = confirmationDialogsConfigConstructor({
          item: {},
          action: DeviceSettingsActions.ENABLE_USER_BASED_AUTH,
          callback: () => {
            localValue.value.enableUserBasedAuthentication =
              !localValue.value.enableUserBasedAuthentication;
          },
        });

        dialogsStore.openDialog({
          ...dialogConfig,
          header: {
            ...dialogConfig.header,
            titleClass: "text-red-dark",
            titleIcon: "warning",
          },
          disable: false,
        });
      }
    };

    watch(
      localValue,
      (newVal) => {
        emit("update:data", {
          ...props.data,
          [GlobalPolicySection.DEVICE_SETTINGS]: newVal,
        });
      },
      { immediate: true, deep: true }
    );

    return {
      localValue,
      onUserBasedAuthClicked,
      availableLabels,
    };
  },
});
</script>

<style scoped lang="scss"></style>
